import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { removeFromFavorites, clearFavorites } from '../redux/menu/favoritesSlice';
import { FaHeart, FaTrash, FaWhatsapp } from 'react-icons/fa';
import { HiOutlineHeart } from 'react-icons/hi';
import Back from '../components/Common/Back';
import useTheme from '../hooks/useTheme';
import useBackgroundImage from '../hooks/useBackgroundImage';
import { getFullScreenBackgroundStyle } from '../utils/backgroundStyles';

const FavoritesScreen = () => {
  const favorites = useSelector((state) => state.favorites.favorites);
  const { profile } = useSelector((state) => state.profile);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const backgroundImage = useBackgroundImage();

  const handleRemoveFavorite = (productId) => {
    dispatch(removeFromFavorites(productId));
  };

  const handleClearAll = () => {
    dispatch(clearFavorites());
  };

  const handleCardClick = (productId) => {
    navigate(`/${id}/product/${productId}`);
  };

  const handleEnquiry = (product) => {
    if (!profile?.whatsapp) return;
    
    const companyName = profile?.name || 'Our Company';
    const companyLocation = profile?.address || '';
    
    // Create enquiry message (without property link for favorites screen)
    let message = `🏠 *PROPERTY ENQUIRY*\n\n`;
    message += `📋 *PROPERTY DETAILS*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `Property: *${product?.name}*\n`;
    message += `Price: *${product.currency} ${parseFloat(product?.price || 0).toFixed(2).replace('.00', '')}*\n`;
    
    if (product?.description) {
      message += `Description: ${product.description}\n`;
    }
    
    if (product?.brand) {
      message += `Developers: ${product.brand}\n`;
    }
    
    if (product?.model) {
      message += `Code: ${product.model}\n`;
    }
    
    if (product?.warranty) {
      message += `Warranty: ${product.warranty}\n`;
    }
    
    message += `\n🏢 *COMPANY INFORMATION*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `Company: ${companyName}\n`;
    if (companyLocation) {
      message += `Location: ${companyLocation}\n`;
    }
    message += `\n`;
    
    message += `❓ *ENQUIRY*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `I'm interested in this property. Please provide more information about:\n`;
    message += `• Availability\n`;
    message += `• Delivery options\n`;
    message += `• Any special offers\n`;
    message += `• Payment methods\n\n`;
    
    message += `Thank you! 🙏`;
    
    // Open WhatsApp with the message
    const whatsappUrl = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  if (favorites.length === 0) {
    return (
      <div 
        className="min-h-screen p-4"
        style={getFullScreenBackgroundStyle(backgroundImage, theme)}
      >
        <div className="max-w-6xl mx-auto pt-8 md:pt-32">
          {/* Back Button */}
          <div className="mb-8">
            <Back />
          </div>
          
          <div className="text-center max-w-md mx-auto">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg"
                 style={{ backgroundColor: '#fce7f3' }}>
              <HiOutlineHeart className="w-12 h-12" style={{ color: '#ec4899' }} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
              No Favorites Yet
            </h1>
            <p className="text-gray-600 mb-6">
              Start adding items to your favorites by clicking the heart icon on any product!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen p-4"
      style={getFullScreenBackgroundStyle(backgroundImage, theme)}
    >
      <div className="max-w-6xl mx-auto pt-8 md:pt-32">
        {/* Back Button */}
        <div className="mb-8">
          <Back />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              My Favorites
            </h1>
          </div>
          
          {favorites.length > 0 && (
            <button
              onClick={handleClearAll}
              className="group relative flex items-center space-x-2 px-4 py-2 rounded-full font-medium transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#dc2626',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
                e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                e.target.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-red-100 group-hover:bg-red-200 transition-colors duration-200">
                <FaTrash className="w-3 h-3 text-red-600" />
              </div>
              <span className="text-sm">Clear All</span>
            </button>
          )}
        </div>

        {/* Favorites Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {favorites.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              onClick={() => handleCardClick(product.id)}
            >
              {/* Product Image */}
              <div className="relative">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
                
                {/* Remove from Favorites Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFavorite(product.id);
                  }}
                  className="absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-pink-200"
                  style={{
                    backgroundColor: '#ec4899',
                    color: 'white'
                  }}
                  title="Remove from favorites"
                >
                  <FaHeart className="w-5 h-5" />
                </button>
              </div>

              {/* Product Details */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">
                  {product.name}
                </h3>
                
                {product.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {product.description}
                  </p>
                )}

                {/* Price */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xl font-bold text-gray-900">
                    {product.currency} {parseFloat(product.price).toFixed(2).replace('.00', '')}
                  </span>
                  
                  {product.is_veg !== undefined && (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      product.is_veg ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${
                        product.is_veg ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEnquiry(product);
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 hover:shadow-lg"
                    style={{
                      backgroundColor: '#1f2937',
                      color: 'white',
                      border: '2px solid #1f2937',
                      opacity: 1,
                      display: 'flex'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#374151';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1f2937';
                    }}
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>Enquiry</span>
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFavorite(product.id);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Remove
                  </button>
                </div>

                {/* Favorited Date */}
                <div className="mt-3 text-xs text-gray-500">
                  Added {new Date(product.favoritedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FavoritesScreen;
